import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

const AuthContext = createContext({
    user: null,
    login: () => { },
    logout: () => { },
    updateProfile: () => { }
})

const STORAGE_KEY = 'finanuAuth'
const STEP_STORAGE_KEY = 'finanu.dashboard.activeStep'
const SESSION_DURATION = 2 * 60 * 60 * 1000 // 2 hours in milliseconds

const safeParse = (value) => {
    try {
        return JSON.parse(value || 'null')
    } catch (error) {
        console.error('Failed to parse auth payload', error)
        return null
    }
}

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(() => safeParse(localStorage.getItem(STORAGE_KEY)))

    const persistUser = useCallback((payload) => {
        if (!payload) {
            localStorage.removeItem(STORAGE_KEY)
            return
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
    }, [])

    const login = useCallback((payload) => {
        if (!payload?.token) {
            console.warn('Invalid auth payload provided.')
            return
        }
        const sanitizedPayload = {
            user_id: payload.user_id ?? payload.userId ?? payload.profile?.user_id ?? payload.profile?.id ?? null,
            email: payload.email ?? payload.profile?.email ?? null,
            token: payload.token,
            role: payload.role ?? 'user',
            profile: payload.profile ?? null,
            loginTime: Date.now()
        }
        setUser(sanitizedPayload)
        persistUser(sanitizedPayload)
    }, [persistUser])

    const logout = useCallback(() => {
        setUser(null)
        persistUser(null)

        if (typeof window !== 'undefined') {
            try {
                window.localStorage.removeItem(STEP_STORAGE_KEY)
            } catch (error) {
                console.warn('Failed to clear dashboard step state', error)
            }
        }
    }, [persistUser])

    const updateProfile = useCallback((patch = {}) => {
        if (!patch || typeof patch !== 'object') {
            return
        }

        setUser((prev) => {
            if (!prev) {
                return prev
            }

            const nextUser = { ...prev }

            if (Object.prototype.hasOwnProperty.call(patch, 'email')) {
                nextUser.email = patch.email
            }

            nextUser.profile = {
                ...(prev.profile || {}),
                ...patch
            }

            persistUser(nextUser)
            return nextUser
        })
    }, [persistUser])

    // Check session expiration on mount and periodically
    useEffect(() => {
        const checkSessionExpiration = () => {
            if (user?.loginTime) {
                const elapsed = Date.now() - user.loginTime
                if (elapsed >= SESSION_DURATION) {
                    console.log('Session expired after 24 hours')
                    logout()
                }
            }
        }

        // Check immediately on mount
        checkSessionExpiration()

        // Check every minute
        const intervalId = setInterval(checkSessionExpiration, 60 * 1000)

        return () => clearInterval(intervalId)
    }, [user, logout])

    const value = useMemo(() => ({
        user,
        login,
        logout,
        updateProfile,
        isAuthenticated: Boolean(user?.token),
        isAdmin: user?.role === 'admin'
    }), [user, login, logout, updateProfile])

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => useContext(AuthContext)
